/**
 * PatientDetailPanel
 *
 * Extração do dialog de detalhes de Patients.tsx em um componente reutilizável.
 * Toda a lógica de estado, busca de dados e JSX foi movida para cá.
 *
 * Usado em:
 *  - Patients.tsx  → dentro do Dialog existente
 *  - Agenda.tsx    → lado a lado com o formulário de agendamento
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Avatar, Chip, IconButton, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert,
  Tooltip, Divider, Tabs, Tab, Grid, Card, CardContent, Paper,
  Stack, Badge, Collapse, useTheme, useMediaQuery, Dialog,
  DialogTitle, DialogContent, DialogActions, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Close as CloseIcon, Edit as EditIcon,
  CalendarToday as CalendarIcon, LocalHospital as HospitalIcon,
  FolderOpen as FolderIcon, AttachMoney as MoneyIcon, Warning as WarningIcon,
  Favorite as HeartIcon, Medication as MedicationIcon, Science as ScienceIcon,
  Description as DocIcon, TrendingUp as TrendingIcon, Schedule as ScheduleIcon,
  EventAvailable as EventIcon, AccessTime as TimeIcon,
  Cake as CakeIcon, Bloodtype as BloodIcon, EventNote as EventNoteIcon,
  OpenInNew as OpenInNewIcon, UploadFile as UploadFileIcon, Download as DownloadIcon,
  AttachFile as AttachFileIcon, InsertDriveFile as FileIcon, Image as ImageIcon,
  ZoomIn as ZoomInIcon, Star as StarIcon, Folder as FolderDocIcon,
  Timeline as TimelineIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  MedicalServices as MedicalIcon, PlayArrow as PlayIcon, Undo as UndoIcon,
  CheckCircle as CheckCircleIcon, Inventory as InventoryIcon,
  Refresh as RefreshIcon, Add as AddIcon, Person as PersonIcon,
  Phone as PhoneIcon, Print as PrintIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import { handleCashRegisterRequired } from '../utils/cashRegisterPrompt';
import { resolveEvolutionEntryFormat } from '../utils/clinicEvolutionFormat';
import PatientAvatarUpload from './PatientAvatarUpload';
import ClinicalHtmlField from './ClinicalHtmlField';
import HealthcarePanel from './HealthcarePanel';
import HealthcareEvolutionDialog from './HealthcareEvolutionDialog';
import { clinicalHtmlToPlainPreview } from '../utils/clinicalHtml';
import { useAuth } from '../contexts/AuthContext';
import {
  evolutionViewerHasFollowUpSection,
  evolutionPrintableListLabel,
  formatEvolutionReturnWorkflowLines,
  getEvolutionReturnWorkflow,
  getEvolutionWorkflowPrintables,
  normalizeEvolutionProcedureLabels,
} from './evolution/evolutionViewHelpers';

/* ═══════════════════════════════════════════════════════════════
   HELPERS (mesmos de Patients.tsx)
   ═══════════════════════════════════════════════════════════════ */

const getStatusConfig = (status: string) => {
  const configs: Record<string, { label: string; bg: string; color: string }> = {
    scheduled:   { label: 'Agendado',   bg: '#e8f0fe', color: '#1d4ed8' },
    confirmed:   { label: 'Confirmado', bg: '#e6f7f5', color: '#0f766e' },
    waiting:     { label: 'Na espera',  bg: '#fef6e8', color: '#b45309' },
    in_progress: { label: 'Em Atend.',  bg: '#f3effc', color: '#6d28d9' },
    attended:    { label: 'Atendido',   bg: '#e6f7f5', color: '#0d9488' },
    completed:   { label: 'Concluído',  bg: '#ecfdf3', color: '#15803d' },
    rescheduled: { label: 'Reagendado', bg: '#fff4ed', color: '#c2410c' },
    cancelled:   { label: 'Cancelado',  bg: '#fef2f2', color: '#b91c1c' },
    no_show:     { label: 'Faltou',     bg: '#f1f5f9', color: '#64748b' },
    draft:       { label: 'Rascunho',   bg: '#f3f4f6', color: '#6b7280' },
    sent:        { label: 'Enviado',    bg: '#dbeafe', color: '#1d4ed8' },
    approved:    { label: 'Aprovado',   bg: '#d1fae5', color: '#065f46' },
    rejected:    { label: 'Recusado',   bg: '#fee2e2', color: '#dc2626' },
    active:      { label: 'Ativo',      bg: '#d1fae5', color: '#059669' },
    pending:     { label: 'Pendente',   bg: '#fef3c7', color: '#d97706' },
    paid:        { label: 'Pago',       bg: '#d1fae5', color: '#059669' },
    overdue:     { label: 'Vencido',    bg: '#fee2e2', color: '#dc2626' },
  };
  return configs[status] || { label: status, bg: '#f3f4f6', color: '#6b7280' };
};

const getGenderLabel = (g: string) => {
  const m: Record<string, string> = {
    masculine: 'Masculino', feminine: 'Feminino', other: 'Outro', prefer_not_say: 'Prefiro não dizer',
  };
  return m[g] || g || '-';
};

const getDocTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    contract: 'Contrato', id_document: 'Doc. de Identidade', insurance_card: 'Carteirinha',
    exam_result: 'Resultado de Exame', xray: 'Raio-X', photo: 'Foto',
    consent_form: 'Termo de Consentimento', other: 'Outro',
  };
  return labels[type] || type;
};

const getDocTypeColor = (type: string) => {
  const colors: Record<string, string> = {
    contract: '#2563eb', id_document: '#7c3aed', insurance_card: '#059669',
    exam_result: '#d97706', xray: '#475569', photo: '#ec4899',
    consent_form: '#14B8A6', other: '#6b7280',
  };
  return colors[type] || '#6b7280';
};

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '-';
  try { return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR'); } catch { return '-'; }
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const escapeHtml = (raw: unknown) =>
  String(raw ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Para atributo HTML (ex.: src da logo) — não usar escapeHtml inteiro (&quot; quebraria o atributo). */
const escapeAttr = (raw: unknown) =>
  String(raw ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/* ── Constantes financeiras (mesmo conjunto usado em Financial/AccountsReceivable) ── */
const RECEIVABLE_CATEGORIES: { value: string; label: string }[] = [
  { value: 'consulta_medica', label: 'Consulta Médica' },
  { value: 'procedimento', label: 'Procedimento' },
  { value: 'exame', label: 'Exame' },
  { value: 'cirurgia', label: 'Cirurgia' },
  { value: 'retorno', label: 'Retorno' },
  { value: 'avaliacao', label: 'Avaliação' },
  { value: 'tratamento', label: 'Tratamento Estético' },
  { value: 'convenio', label: 'Convênio / Plano' },
  { value: 'particular', label: 'Particular' },
  { value: 'venda_produtos', label: 'Venda de Produtos' },
  { value: 'outros', label: 'Outros' },
];

const RECEIVABLE_PAYMENT_METHODS: { value: string; label: string }[] = [
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'pix', label: 'PIX' },
  { value: 'cartao_credito', label: 'Cartão de Crédito' },
  { value: 'cartao_debito', label: 'Cartão de Débito' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'transferencia', label: 'Transferência' },
  { value: 'convenio', label: 'Convênio / Plano' },
  { value: 'cheque', label: 'Cheque' },
];

const getReceivableStatusInfo = (s: string) => {
  switch (s) {
    case 'paid': return { label: 'Recebido', bg: '#d1fae5', color: '#059669' };
    case 'partial': return { label: 'Parcial', bg: '#dbeafe', color: '#1d4ed8' };
    case 'overdue': return { label: 'Atrasado', bg: '#fee2e2', color: '#dc2626' };
    case 'cancelled': return { label: 'Cancelado', bg: '#f3f4f6', color: '#6b7280' };
    case 'pending':
    default:
      return { label: 'Pendente', bg: '#fef3c7', color: '#d97706' };
  }
};

const getPaymentMethodLabel = (v: string) =>
  RECEIVABLE_PAYMENT_METHODS.find(m => m.value === v)?.label || v || '-';

const calculateAge = (birthDate?: string) => {
  if (!birthDate) return '';
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return `${age} anos`;
};

/* ═══════════════════════════════════════════════════════════════
   TIPOS
   ═══════════════════════════════════════════════════════════════ */

export interface PatientDetailPanelProps {
  patientId: string;
  /** Dados iniciais já carregados (evita fetch extra). */
  initialPatient?: any;
  onClose?: () => void;
  /** Recebe o paciente atual do painel (mesmo fluxo de Pacientes: lápis = editar ficha). */
  onEdit?: (patient: any) => void;
  /** Chamado quando o avatar é atualizado (para o pai sincronizar a lista). */
  onPatientUpdated?: (patient: any) => void;
  /**
   * Quando fornecido, o botão "Orçamento" chama esta função em vez de abrir nova aba.
   * Recebe o objeto do paciente para pré-preencher o formulário de orçamento.
   */
  onNewBudget?: (patient: any) => void;
  /** Incrementado pelo pai após salvar um orçamento (ex.: agenda) para recarregar KPIs/listas sem recarregar a página. */
  summaryReloadToken?: number;
}

/* ═══════════════════════════════════════════════════════════════
   COMPONENTE
   ═══════════════════════════════════════════════════════════════ */

const PatientDetailPanel: React.FC<PatientDetailPanelProps> = ({
  patientId,
  initialPatient,
  onClose,
  onEdit,
  onPatientUpdated,
  onNewBudget,
  summaryReloadToken = 0,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const navigate = useNavigate();
  // ── Estado ──
  const [patient, setPatient] = useState<any>(initialPatient || null);
  const [healthcareSummary, setHealthcareSummary] = useState<any>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [expandedHistoryRow, setExpandedHistoryRow] = useState<string | null>(null);
  const [expandedEvolutionId, setExpandedEvolutionId] = useState<string | null>(null);
  const [snackMsg, setSnackMsg] = useState('');

  // Documentos
  const [documents, setDocuments] = useState<any[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [showDocUpload, setShowDocUpload] = useState(false);
  const [docUploadForm, setDocUploadForm] = useState({ title: '', document_type: 'contract', description: '' });
  const [docFile, setDocFile] = useState<File | null>(null);
  const [docUploading, setDocUploading] = useState(false);
  const docFileInputRef = useRef<HTMLInputElement>(null);

  // Imagens
  const [patientImages, setPatientImages] = useState<any[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);

  // Healthcare Panel / Evolution
  const [healthcarePanelOpen, setHealthcarePanelOpen] = useState(false);
  const [evolutionDialogOpen, setEvolutionDialogOpen] = useState(false);
  const [evolutionExistingId, setEvolutionExistingId] = useState<string | null>(null);

  // Procedimentos
  const [patientProcedures, setPatientProcedures] = useState<{ pending: any[]; approved: any[]; completed: any[] }>({ pending: [], approved: [], completed: [] });
  const [proceduresLoading, setProceduresLoading] = useState(false);

  // Financeiro — contas a receber do paciente
  const [receivables, setReceivables] = useState<any[]>([]);
  const [receivablesLoading, setReceivablesLoading] = useState(false);

  // Dialog: criar conta a receber
  const [createReceivableOpen, setCreateReceivableOpen] = useState(false);
  const [creatingReceivable, setCreatingReceivable] = useState(false);
  const todayStr = () => new Date().toISOString().slice(0, 10);
  const emptyReceivableForm = () => ({
    description: '',
    category: 'consulta_medica',
    amount: '',
    payment_method: 'dinheiro',
    due_date: todayStr(),
    notes: '',
    receive_now: true,
  });
  const [receivableForm, setReceivableForm] = useState(emptyReceivableForm());

  // Dialog: registrar recebimento de uma conta existente
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [receivingReceivable, setReceivingReceivable] = useState(false);
  const [activeReceivable, setActiveReceivable] = useState<any | null>(null);
  const [receiveForm, setReceiveForm] = useState({
    amount: '',
    payment_method: 'dinheiro',
    payment_date: todayStr(),
    notes: '',
  });

  // Executar procedimento
  const [executeDialogOpen, setExecuteDialogOpen] = useState(false);
  const [executingItem, setExecutingItem] = useState<any>(null);
  const [executeForm, setExecuteForm] = useState({ professional_id: '', notes: '' });
  const [executeSaving, setExecuteSaving] = useState(false);
  const [professionals, setProfessionals] = useState<any[]>([]);

  // ── Funções de dados ──

  const fetchSummary = useCallback(async () => {
    if (!patientId) return;
    setSummaryLoading(true);
    try {
      const res = await axios.get(getApiUrl(`/clinic/healthcare-panel/patient/${patientId}/summary`));
      if (res.data.success) {
        setHealthcareSummary(res.data.data);
        if (res.data.data?.patient) {
          setPatient((prev: any) => ({ ...prev, ...res.data.data.patient }));
        }
      }
    } catch (err) {
      console.error('Error fetching patient summary:', err);
    } finally {
      setSummaryLoading(false);
    }
  }, [patientId]);

  const loadDocuments = useCallback(async () => {
    if (!patientId) return;
    setDocsLoading(true);
    try {
      const { data: res } = await axios.get(getApiUrl(`/clinic/patients/${patientId}/documents`));
      if (res.success) setDocuments(res.data || []);
    } catch (err) { console.error('Error loading documents:', err); }
    finally { setDocsLoading(false); }
  }, [patientId]);

  const loadPatientImages = useCallback(async () => {
    if (!patientId) return;
    setImagesLoading(true);
    try {
      const { data: res } = await axios.get(getApiUrl('/clinic/patient-images'), { params: { patient_id: patientId } });
      if (res.success) setPatientImages(res.data || []);
    } catch (err) { console.error('Error loading images:', err); }
    finally { setImagesLoading(false); }
  }, [patientId]);

  const loadPatientProcedures = useCallback(async () => {
    if (!patientId) return;
    setProceduresLoading(true);
    try {
      const { data: body } = await axios.get(getApiUrl(`/clinic/patients/${patientId}/procedures`));
      const payload = body?.data ?? body;
      if (payload && typeof payload === 'object' && Array.isArray((payload as any).pending)) {
        setPatientProcedures({
          pending: (payload as any).pending || [],
          approved: (payload as any).approved || [],
          completed: (payload as any).completed || [],
        });
      } else {
        setPatientProcedures({ pending: [], approved: [], completed: [] });
      }
    } catch { setPatientProcedures({ pending: [], approved: [], completed: [] }); }
    finally { setProceduresLoading(false); }
  }, [patientId]);

  const loadProfessionals = useCallback(async () => {
    try {
      const { data: res } = await axios.get(getApiUrl('/clinic/professionals'), { params: { is_active: true, limit: 200 } });
      setProfessionals(res.data || []);
    } catch { /* ignore */ }
  }, []);

  /* ── Contas a receber do paciente ── */
  const loadReceivables = useCallback(async () => {
    if (!patientId) return;
    setReceivablesLoading(true);
    try {
      const { data: res } = await axios.get(
        getApiUrl('/financial-module/accounts-receivable'),
        { params: { patient_id: patientId, limit: 100 } },
      );
      setReceivables(res?.data || []);
    } catch (err) {
      console.error('Error loading receivables:', err);
      setReceivables([]);
    } finally {
      setReceivablesLoading(false);
    }
  }, [patientId]);

  const openCreateReceivable = () => {
    setReceivableForm(emptyReceivableForm());
    setCreateReceivableOpen(true);
  };

  const submitCreateReceivable = async () => {
    if (!patient) return;
    const amount = parseFloat(receivableForm.amount);
    if (!receivableForm.description.trim()) { setSnackMsg('Descrição é obrigatória'); return; }
    if (!Number.isFinite(amount) || amount <= 0) { setSnackMsg('Valor inválido'); return; }
    if (!receivableForm.due_date) { setSnackMsg('Vencimento é obrigatório'); return; }
    setCreatingReceivable(true);
    try {
      const createPayload: Record<string, unknown> = {
        description: receivableForm.description.trim(),
        category: receivableForm.category || undefined,
        amount,
        payment_method: receivableForm.payment_method || undefined,
        due_date: receivableForm.due_date,
        notes: receivableForm.notes || undefined,
        patient_id: patient.id,
        patient_name: patient.full_name,
        status: 'pending',
        ...(user?.current_unit?.id ? { unit_id: user.current_unit.id } : {}),
        _user_id: user?.id,
        _user_name: user?.full_name,
      };
      const { data: createRes } = await axios.post(
        getApiUrl('/financial-module/accounts-receivable'),
        createPayload,
      );
      const created = createRes?.data;

      // Se "Receber agora" estiver marcado: atualiza para paid, dispara o lançamento no caixa do usuário
      if (receivableForm.receive_now && created?.id) {
        await axios.put(
          getApiUrl(`/financial-module/accounts-receivable/${created.id}`),
          {
            status: 'paid',
            amount_paid: amount,
            payment_date: todayStr(),
            payment_method: receivableForm.payment_method || undefined,
            _user_id: user?.id,
            _user_name: user?.full_name,
          },
        );
        setSnackMsg('Conta criada e recebida — lançado no seu caixa');
      } else {
        setSnackMsg('Conta a receber criada');
      }
      setCreateReceivableOpen(false);
      void loadReceivables();
      void fetchSummary();
    } catch (err: any) {
      if (handleCashRegisterRequired(err, (p) => navigate(p))) {
        setCreatingReceivable(false);
        return;
      }
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Erro ao criar conta a receber';
      setSnackMsg(msg);
    } finally {
      setCreatingReceivable(false);
    }
  };

  const openReceiveDialog = (rec: any) => {
    const remaining = Math.max(0, (Number(rec.amount) || 0) - (Number(rec.amount_paid || rec.paid_amount) || 0));
    setActiveReceivable(rec);
    setReceiveForm({
      amount: (remaining || rec.amount || 0).toString(),
      payment_method: rec.payment_method || 'dinheiro',
      payment_date: todayStr(),
      notes: '',
    });
    setReceiveDialogOpen(true);
  };

  const submitReceive = async () => {
    if (!activeReceivable) return;
    const amount = parseFloat(receiveForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) { setSnackMsg('Valor inválido'); return; }
    setReceivingReceivable(true);
    try {
      const totalDue = Number(activeReceivable.amount) || 0;
      const alreadyPaid = Number(activeReceivable.amount_paid || activeReceivable.paid_amount) || 0;
      const newTotalPaid = alreadyPaid + amount;
      const newStatus = newTotalPaid >= totalDue ? 'paid' : 'partial';
      await axios.put(
        getApiUrl(`/financial-module/accounts-receivable/${activeReceivable.id}`),
        {
          status: newStatus,
          amount_paid: newTotalPaid,
          payment_date: receiveForm.payment_date,
          payment_method: receiveForm.payment_method || undefined,
          payment_notes: receiveForm.notes || undefined,
          _user_id: user?.id,
          _user_name: user?.full_name,
        },
      );
      const msg = newStatus === 'paid'
        ? 'Recebimento total registrado — lançado no seu caixa'
        : `Recebimento parcial (${formatCurrency(amount)}) registrado — saldo pendente: ${formatCurrency(Math.max(0, totalDue - newTotalPaid))}`;
      setSnackMsg(msg);
      setReceiveDialogOpen(false);
      setActiveReceivable(null);
      void loadReceivables();
      void fetchSummary();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Erro ao registrar recebimento';
      setSnackMsg(msg);
    } finally {
      setReceivingReceivable(false);
    }
  };

  const deleteReceivable = async (rec: any) => {
    if (!rec?.id) return;
    if (!window.confirm('Excluir esta conta a receber?')) return;
    try {
      await axios.delete(getApiUrl(`/financial-module/accounts-receivable/${rec.id}`));
      setSnackMsg('Conta a receber excluída');
      void loadReceivables();
      void fetchSummary();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Erro ao excluir';
      setSnackMsg(msg);
    }
  };

  // ── Effects ──

  useEffect(() => {
    if (!patientId) return;
    setPatient(initialPatient || null);
    setHealthcareSummary(null);
    setTabValue(0);
    setExpandedHistoryRow(null);
    setExpandedEvolutionId(null);
    void fetchSummary();
  }, [patientId, fetchSummary]);

  useEffect(() => {
    if (!patientId || summaryReloadToken < 1) return;
    void fetchSummary();
    void loadReceivables();
  }, [summaryReloadToken, patientId, fetchSummary, loadReceivables]);

  useEffect(() => {
    if (tabValue === 4) loadReceivables();
    if (tabValue === 5) loadDocuments();
    if (tabValue === 6) loadPatientImages();
    if (tabValue === 7) loadPatientProcedures();
  }, [tabValue, loadReceivables, loadDocuments, loadPatientImages, loadPatientProcedures]);

  useEffect(() => {
    if (tabValue !== 3) setExpandedEvolutionId(null);
  }, [tabValue]);

  // ── Handlers ──

  const handleOpenHealthcarePanel = () => {
    if (!patient?.contact_id) {
      setSnackMsg('Paciente sem contato vinculado para abrir o Healthcare Panel');
      return;
    }
    setHealthcarePanelOpen(true);
  };

  const handleProcedureItemStatusChange = async (itemId: string, status: string) => {
    try {
      await axios.patch(getApiUrl(`/clinic/treatment-plan-items/${itemId}/status`), { status });
      loadPatientProcedures();
    } catch (err: any) {
      setSnackMsg(err.response?.data?.error || 'Erro ao atualizar status');
    }
  };

  const handleExecuteProcedure = (item: any) => {
    setExecutingItem(item);
    setExecuteForm({ professional_id: '', notes: '' });
    if (professionals.length === 0) loadProfessionals();
    setExecuteDialogOpen(true);
  };

  const confirmExecute = async () => {
    if (!executingItem || !executeForm.professional_id) return;
    setExecuteSaving(true);
    try {
      const { data: res } = await axios.post(getApiUrl(`/clinic/treatment-plan-items/${executingItem.id}/execute`), executeForm);
      const warnings = res.data?.stock_warnings || [];
      setSnackMsg(warnings.length > 0
        ? `Executado! Alertas de estoque: ${warnings.join('; ')}`
        : 'Procedimento executado com sucesso!');
      setExecuteDialogOpen(false);
      loadPatientProcedures();
    } catch (err: any) {
      setSnackMsg(err.response?.data?.error || 'Erro ao executar procedimento');
    } finally { setExecuteSaving(false); }
  };

  const handleUndoExecution = async (itemId: string) => {
    if (!window.confirm('Desfazer a execução deste procedimento? Os materiais consumidos serão devolvidos ao estoque.')) return;
    try {
      await axios.post(getApiUrl(`/clinic/treatment-plan-items/${itemId}/undo-execution`));
      setSnackMsg('Execução desfeita com sucesso');
      loadPatientProcedures();
    } catch (err: any) {
      setSnackMsg(err.response?.data?.error || 'Erro ao desfazer');
    }
  };

  const handleDocUpload = async () => {
    if (!patient || !docFile) return;
    setDocUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', docFile);
      fd.append('title', docUploadForm.title || docFile.name);
      fd.append('document_type', docUploadForm.document_type);
      if (docUploadForm.description) fd.append('description', docUploadForm.description);
      const { data: res } = await axios.post(
        getApiUrl(`/clinic/patients/${patientId}/documents/upload`), fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      if (res.success) {
        setDocuments(prev => [res.data, ...prev]);
        setShowDocUpload(false);
        setDocFile(null);
        setDocUploadForm({ title: '', document_type: 'contract', description: '' });
        setSnackMsg('Documento anexado com sucesso!');
      }
    } catch { setSnackMsg('Erro ao fazer upload do documento'); }
    finally { setDocUploading(false); }
  };

  // ── Print ──

  const printPatient = async () => {
    if (!patient) return;
    const p = patient;
    const s = healthcareSummary;
    let clinic: Record<string, unknown> = {};
    try {
      const { data: body } = await axios.get<{ data?: Record<string, unknown> }>(getApiUrl('/clinic/settings'));
      if (body?.data && typeof body.data === 'object') clinic = body.data;
    } catch {
      /* imprime sem cabeçalho da clínica */
    }

    const allergies = (s?.patient?.allergies || p.allergies || []) as string[];
    const conditions = (s?.patient?.chronic_conditions || p.chronic_conditions || []) as string[];
    const meds = (s?.patient?.medications || p.medications || []) as string[];

    const sheetVisitCount =
      s != null ? Number(s.stats?.completed_appointments) || 0 : Number(p.total_visits) || 0;
    const sheetTotalInvested =
      s != null ? Number(s.financial?.total_paid) || 0 : Number(p.total_spent) || 0;

    const logoUrl = typeof clinic.logo_url === 'string' && clinic.logo_url.trim() ? clinic.logo_url.trim() : '';
    const logoBlock = logoUrl
      ? `<div class="doc-head-logo"><img src="${escapeAttr(logoUrl)}" alt="" /></div>`
      : '';
    const clinicName = escapeHtml(clinic.clinic_name || 'Clínica');
    const clinicSub = clinic.clinic_subtitle ? `<div class="clinic-subtitle">${escapeHtml(clinic.clinic_subtitle)}</div>` : '';
    const metaParts: string[] = [];
    if (clinic.cnpj) metaParts.push(`CNPJ: ${escapeHtml(clinic.cnpj)}`);
    const addr = [clinic.address_street, clinic.address_number, clinic.address_neighborhood, clinic.address_city, clinic.address_state]
      .filter(Boolean)
      .join(', ');
    if (addr) metaParts.push(escapeHtml(addr));
    const tel = clinic.phone || clinic.whatsapp;
    if (tel) metaParts.push(escapeHtml(tel));
    if (clinic.email) metaParts.push(escapeHtml(clinic.email));
    const metaHtml = metaParts.length ? metaParts.join('<br/>') : '';

    const letterhead = `
<div class="doc-head">${logoBlock}
  <div class="doc-head-center"><div class="clinic-name">${clinicName}</div>${clinicSub}</div>
  <div class="doc-head-meta">${metaHtml}</div>
</div>`;

    const html = `<!doctype html><html><head><meta charset="utf-8">
<title>Ficha — ${escapeHtml(p.full_name)}</title>
<style>
body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;padding:28px 36px;color:#111;font-size:13px}
.doc-head{display:flex;align-items:flex-start;justify-content:space-between;gap:14px;margin-bottom:16px;padding-bottom:12px;border-bottom:3px solid #14B8A6}
.doc-head-logo{flex-shrink:0}
.doc-head-logo img{display:block;max-width:150px;max-height:56px;width:auto;height:auto;object-fit:contain;object-position:left top}
.doc-head-center{flex:1;min-width:0;text-align:center;align-self:center}
.doc-head-meta{flex-shrink:0;max-width:42%;font-size:10px;color:#64748b;text-align:right;line-height:1.45}
.doc-head .clinic-name{font-size:18px;font-weight:700;color:#0d9488;margin:0}
.clinic-subtitle{font-size:11px;color:#6b7280;margin-top:2px}
.patient-sheet-title{font-size:22px;margin:4px 0 2px;color:#0d9488;font-weight:700}
.sub{color:#64748b;font-size:13px;margin-bottom:4px}
h2{font-size:13px;font-weight:700;border-bottom:2px solid #14B8A6;padding-bottom:3px;margin:18px 0 8px;color:#0d9488;text-transform:uppercase;letter-spacing:.5px}
.grid{display:grid;grid-template-columns:repeat(2,1fr);gap:4px 20px;margin-bottom:8px}
.field{margin-bottom:4px}
.label{color:#6b7280;font-size:11px;display:block;font-weight:600}
.alert{background:#fef3c7;border:1px solid #fde68a;border-radius:4px;padding:6px 10px;margin:6px 0;font-size:12px}
.alert.red{background:#fee2e2;border-color:#fecaca}
.alert.blue{background:#dbeafe;border-color:#bfdbfe}
table{width:100%;border-collapse:collapse;font-size:12px;margin-top:4px}
th,td{border:1px solid #e5e7eb;padding:5px 8px;text-align:left}
th{background:#f9fafb;font-weight:600}
.chip{display:inline-block;padding:1px 7px;border-radius:10px;font-size:11px;font-weight:600;background:#d1fae5;color:#059669;margin:2px}
.chip.red{background:#fee2e2;color:#dc2626}
.chip.yellow{background:#fef3c7;color:#d97706}
.chip.blue{background:#dbeafe;color:#1d4ed8}
@media print{@page{margin:20px 24px}}
</style></head><body>
${letterhead}
<div class="patient-sheet-title">${escapeHtml(p.full_name)}</div>
${p.social_name ? `<div class="sub">Nome social: ${escapeHtml(p.social_name)}</div>` : ''}
<div class="sub">Ficha gerada em ${new Date().toLocaleString('pt-BR')} &nbsp;|&nbsp; Status: ${p.status === 'active' ? 'Ativo' : 'Inativo'}</div>

<h2>Dados Pessoais</h2>
<div class="grid">
${p.cpf ? `<div class="field"><span class="label">CPF</span>${escapeHtml(p.cpf)}</div>` : ''}
${p.rg ? `<div class="field"><span class="label">RG</span>${escapeHtml(p.rg)}</div>` : ''}
${p.birth_date ? `<div class="field"><span class="label">Data de Nascimento</span>${formatDate(p.birth_date)} (${calculateAge(p.birth_date)})</div>` : ''}
${p.gender ? `<div class="field"><span class="label">Gênero</span>${getGenderLabel(p.gender)}</div>` : ''}
${p.marital_status ? `<div class="field"><span class="label">Estado Civil</span>${escapeHtml(p.marital_status)}</div>` : ''}
${p.profession ? `<div class="field"><span class="label">Profissão</span>${escapeHtml(p.profession)}</div>` : ''}
${(p.patient_source_ref?.name || p.patient_source) ? `<div class="field"><span class="label">Origem do paciente</span>${escapeHtml(p.patient_source_ref?.name || p.patient_source)}</div>` : ''}
${p.blood_type ? `<div class="field"><span class="label">Tipo Sanguíneo</span>${escapeHtml(p.blood_type)}</div>` : ''}
</div>

<h2>Contatos</h2>
<div class="grid">
${p.phone ? `<div class="field"><span class="label">Telefone</span>${escapeHtml(p.phone)}</div>` : ''}
${p.whatsapp ? `<div class="field"><span class="label">WhatsApp</span>${escapeHtml(p.whatsapp)}</div>` : ''}
${p.phone_secondary ? `<div class="field"><span class="label">Tel. Secundário</span>${escapeHtml(p.phone_secondary)}</div>` : ''}
${p.email ? `<div class="field"><span class="label">E-mail</span>${escapeHtml(p.email)}</div>` : ''}
</div>

${(p.address_street || p.address_city) ? `
<h2>Endereço</h2>
<div>${escapeHtml([p.address_street, p.address_number, p.address_complement].filter(Boolean).join(', '))}</div>
<div>${escapeHtml([p.address_neighborhood, p.address_city, p.address_state].filter(Boolean).join(' - '))}${p.address_zip ? ` • CEP: ${escapeHtml(p.address_zip)}` : ''}</div>
` : ''}

<h2>Informações de Saúde</h2>
${p.medical_notes || s?.patient?.medical_notes ? `<div class="field"><span class="label">Obs. Médicas</span>${escapeHtml(p.medical_notes || s?.patient?.medical_notes)}</div>` : ''}
${allergies.length ? `<div class="alert red"><strong>Alergias:</strong> ${escapeHtml(allergies.join(', '))}</div>` : ''}
${conditions.length ? `<div class="alert yellow"><strong>Condições Crônicas:</strong> ${escapeHtml(conditions.join(', '))}</div>` : ''}
${meds.length ? `<div class="alert blue"><strong>Medicamentos em Uso:</strong> ${escapeHtml(meds.join(', '))}</div>` : ''}
${!allergies.length && !conditions.length && !meds.length && !p.medical_notes ? '<div style="color:#9ca3af">Sem alertas de saúde registrados.</div>' : ''}

${(p.clinic_insurances?.name || p.insurance_plan) ? `
<h2>Convênio / Plano de Saúde</h2>
<div class="grid">
<div class="field"><span class="label">Plano</span>${escapeHtml(p.clinic_insurances?.name || p.insurance_plan)}</div>
${p.insurance_number ? `<div class="field"><span class="label">Nº do Cartão</span>${escapeHtml(p.insurance_number)}</div>` : ''}
${p.insurance_validity ? `<div class="field"><span class="label">Validade</span>${formatDate(p.insurance_validity)}</div>` : ''}
</div>` : ''}

${p.emergency_contact_name ? `
<h2>Contato de Emergência</h2>
<div>${escapeHtml(p.emergency_contact_name)}${p.emergency_contact_relation ? ` (${escapeHtml(p.emergency_contact_relation)})` : ''}${p.emergency_contact_phone ? ` — ${escapeHtml(p.emergency_contact_phone)}` : ''}</div>
` : ''}

<h2>Resumo Clínico</h2>
<div class="grid">
<div class="field"><span class="label">Total de Consultas</span>${sheetVisitCount}</div>
<div class="field"><span class="label">Total Investido</span>${formatCurrency(sheetTotalInvested)}</div>
${p.first_visit_date ? `<div class="field"><span class="label">Primeira Consulta</span>${formatDate(p.first_visit_date)}</div>` : ''}
${p.last_visit_date ? `<div class="field"><span class="label">Última Consulta</span>${formatDate(p.last_visit_date)}</div>` : ''}
</div>

${s?.upcoming_appointments?.length ? `
<h2>Próximas Consultas</h2>
<table>
<thead><tr><th>Data</th><th>Hora</th><th>Tipo</th><th>Profissional</th><th>Status</th></tr></thead>
<tbody>
${(s.upcoming_appointments as any[]).map((a: any) => `<tr>
<td>${formatDate(a.appointment_date)}</td>
<td>${escapeHtml(a.start_time?.substring(0, 5) || '-')}</td>
<td>${escapeHtml(a.appointment_type || '-')}</td>
<td>${a.clinic_professionals?.full_name ? `Dr(a). ${escapeHtml(a.clinic_professionals.full_name)}` : '-'}</td>
<td>${escapeHtml(getStatusConfig(a.status).label)}</td>
</tr>`).join('')}
</tbody></table>` : ''}

${s?.recent_appointments?.length ? `
<h2>Consultas Recentes</h2>
<table>
<thead><tr><th>Data</th><th>Hora</th><th>Tipo</th><th>Profissional</th><th>Status</th></tr></thead>
<tbody>
${(s.recent_appointments as any[]).map((a: any) => `<tr>
<td>${formatDate(a.appointment_date)}</td>
<td>${escapeHtml(a.start_time?.substring(0, 5) || '-')}</td>
<td>${escapeHtml(a.title || a.appointment_type || '-')}</td>
<td>${a.clinic_professionals?.full_name ? `Dr(a). ${escapeHtml(a.clinic_professionals.full_name)}` : '-'}</td>
<td>${escapeHtml(getStatusConfig(a.status).label)}</td>
</tr>`).join('')}
</tbody></table>` : ''}

<script>window.onload=()=>setTimeout(()=>window.print(),250)</script>
</body></html>`;

    const w = window.open('', '_blank', 'width=860,height=1000');
    if (w) { w.document.write(html); w.document.close(); }
  };

  // ── Guard ──
  if (!patient && summaryLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#14b8a6' }} />
      </Box>
    );
  }

  if (!patient) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>Paciente não encontrado.</Alert>
    );
  }

  /** KPIs do perfil: vêm do resumo (dados reais no banco), não só das colunas total_visits/total_spent do paciente. */
  const profileConsultCount =
    healthcareSummary != null
      ? Number(healthcareSummary.stats?.completed_appointments ?? 0) || 0
      : Number(patient.total_visits) || 0;
  const profileTotalInvested =
    healthcareSummary != null
      ? Number(healthcareSummary.financial?.total_paid) || 0
      : Number(patient.total_spent) || 0;

  // ── Render ──
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      {/* ── Header ── */}
      <DialogTitle sx={{ p: 0, flexShrink: 0 }}>
        <Box sx={{
          bgcolor: '#fafbfc',
          borderBottom: '1px solid #e8ecf1',
          p: { xs: 2, sm: 2.5 },
          pb: 0,
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 2,
            mb: 2,
          }}>
            {/* Linha 1: avatar + nome (sempre com espaço horizontal completo) */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, sm: 2 }, width: '100%', minWidth: 0 }}>
              <PatientAvatarUpload
                patientId={patient.id}
                currentAvatarUrl={patient.avatar_url}
                patientName={patient.full_name}
                size={isMobile ? 56 : 64}
                onAvatarUpdated={(newUrl) => {
                  const updated = { ...patient, avatar_url: newUrl };
                  setPatient(updated);
                  onPatientUpdated?.(updated);
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <Tooltip title={patient.full_name} placement="bottom-start" enterDelay={500}>
                  <Typography
                    variant={isMobile ? 'h6' : 'h5'}
                    component="div"
                    sx={{
                      fontWeight: 800,
                      color: '#0f172a',
                      lineHeight: 1.25,
                      fontSize: { xs: '1rem', sm: '1.15rem', md: '1.35rem' },
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      wordBreak: 'normal',
                      overflowWrap: 'break-word',
                      hyphens: 'manual',
                    }}
                  >
                    {patient.full_name}
                  </Typography>
                </Tooltip>
                {patient.social_name && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#64748b',
                      mt: 0.35,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Nome social: {patient.social_name}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, mt: 0.8, flexWrap: 'wrap' }}>
                  <Chip
                    label={patient.status === 'active' ? 'Ativo' : 'Inativo'}
                    size="small"
                    sx={{
                      bgcolor: patient.status === 'active' ? '#e6f7f5' : '#f1f5f9',
                      color: patient.status === 'active' ? '#0f766e' : '#64748b',
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      height: 24,
                      borderRadius: '10px',
                    }}
                  />
                  {patient.birth_date && (
                    <Chip
                      label={calculateAge(patient.birth_date)}
                      size="small"
                      sx={{ bgcolor: '#f1f5f9', color: '#475569', fontSize: '0.7rem', height: 24, borderRadius: '10px' }}
                    />
                  )}
                  {(patient.insurance_plan || patient.clinic_insurances?.name) && (
                    <Chip
                      label={patient.clinic_insurances?.name || patient.insurance_plan}
                      size="small"
                      sx={{
                        bgcolor: '#e8f0fe',
                        color: '#1e40af',
                        fontSize: '0.7rem',
                        height: 24,
                        borderRadius: '10px',
                        maxWidth: '100%',
                        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            {/* Linha 2: ações — não competem largura com o nome */}
            <Box sx={{
              display: 'flex', flexWrap: 'wrap', gap: { xs: 0.75, sm: 1 },
              alignItems: 'center', justifyContent: 'flex-start',
              width: '100%',
            }}>
              {/* Imprimir ficha */}
              <Tooltip title="Imprimir Ficha do Paciente">
                <Button size="small" variant="outlined" startIcon={<PrintIcon />}
                  onClick={printPatient}
                  sx={{
                    borderColor: '#cbd5e1',
                    color: '#0f766e',
                    bgcolor: '#fff',
                    textTransform: 'none',
                    borderRadius: 2.5,
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    fontWeight: 600,
                    px: { xs: 1, sm: 1.5 },
                    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                    '&:hover': { borderColor: '#14b8a6', bgcolor: '#f0fdfa' },
                  }}
                >
                  {isMobile ? 'Imprimir' : 'Imprimir Ficha'}
                </Button>
              </Tooltip>

              <Tooltip title="Agendar Consulta">
                <Button size="small" variant="outlined" startIcon={<CalendarIcon />}
                  onClick={() => window.open('/agenda', '_blank')}
                  sx={{
                    borderColor: '#cbd5e1', color: '#0f766e', bgcolor: '#fff', textTransform: 'none', borderRadius: 2.5,
                    fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600, px: { xs: 1, sm: 1.5 },
                    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                    '&:hover': { borderColor: '#14b8a6', bgcolor: '#f0fdfa' },
                  }}
                >
                  {isMobile ? 'Agenda' : 'Agendar'}
                </Button>
              </Tooltip>

              <Tooltip title={onNewBudget ? 'Novo Orçamento (abre aqui mesmo)' : 'Novo Orçamento (paciente já selecionado)'}>
                <Button size="small" variant="outlined" startIcon={<DocIcon />}
                  onClick={() => {
                    if (onNewBudget) {
                      onNewBudget(patient ?? { id: patientId });
                    } else {
                      window.open(`/clinic/budgets?patientId=${encodeURIComponent(patientId)}`, '_blank');
                    }
                  }}
                  sx={{
                    borderColor: '#cbd5e1', color: '#0f766e', bgcolor: '#fff', textTransform: 'none', borderRadius: 2.5,
                    fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600, px: { xs: 1, sm: 1.5 },
                    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                    '&:hover': { borderColor: '#14b8a6', bgcolor: '#f0fdfa' },
                  }}
                >
                  {isMobile ? 'Orç.' : 'Orçamento'}
                </Button>
              </Tooltip>

              <Tooltip title="Nova Receita">
                <Button size="small" variant="outlined" startIcon={<MedicationIcon />}
                  onClick={() => window.open('/clinic/prescriptions', '_blank')}
                  sx={{
                    borderColor: '#cbd5e1', color: '#0f766e', bgcolor: '#fff', textTransform: 'none', borderRadius: 2.5,
                    fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600, px: { xs: 1, sm: 1.5 },
                    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                    '&:hover': { borderColor: '#14b8a6', bgcolor: '#f0fdfa' },
                  }}
                >
                  {isMobile ? 'Receita' : 'Receituário'}
                </Button>
              </Tooltip>

              <Tooltip title="Abrir Healthcare Panel">
                <Button size="small" variant="outlined" startIcon={<HospitalIcon />}
                  onClick={handleOpenHealthcarePanel}
                  sx={{
                    borderColor: '#cbd5e1', color: '#0f766e', bgcolor: '#fff', textTransform: 'none', borderRadius: 2.5,
                    fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600, px: { xs: 1, sm: 1.5 },
                    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                    '&:hover': { borderColor: '#14b8a6', bgcolor: '#f0fdfa' },
                  }}
                >
                  {isMobile ? 'HC' : 'Health Care'}
                </Button>
              </Tooltip>

              {onEdit && (
                <Tooltip title="Editar Paciente">
                  <IconButton size="small" onClick={() => patient && onEdit(patient)}
                    sx={{ color: '#0f766e', bgcolor: '#fff', border: '1px solid #cbd5e1', '&:hover': { bgcolor: '#f0fdfa', borderColor: '#14b8a6' } }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {onClose && (
                <IconButton size="small" onClick={onClose}
                  sx={{ color: '#64748b', bgcolor: '#fff', border: '1px solid #cbd5e1', '&:hover': { bgcolor: '#f1f5f9' } }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Tabs */}
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              mt: 0.5,
              minHeight: 44,
              '& .MuiTab-root': {
                color: '#64748b',
                fontWeight: 600,
                textTransform: 'none',
                minHeight: 44,
                fontSize: { xs: '0.7rem', sm: '0.78rem' },
                px: { xs: 1, sm: 1.5 },
              },
              '& .Mui-selected': { color: '#0f766e !important' },
              '& .MuiTabs-indicator': { bgcolor: '#0f766e', height: 3, borderRadius: '3px 3px 0 0' },
            }}
          >
            <Tab label="Perfil" icon={<PersonIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Agenda" icon={<CalendarIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Histórico" icon={<ScheduleIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Evoluções" icon={<TimelineIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Financeiro" icon={<MoneyIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label={
              <Badge badgeContent={documents.length || 0} color="primary"
                sx={{ '& .MuiBadge-badge': { bgcolor: '#fff', color: '#14B8A6', fontSize: '0.6rem', minWidth: 16, height: 16 } }}>
                Docs
              </Badge>
            } icon={<FolderDocIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label={
              <Badge badgeContent={patientImages.length || 0} color="primary"
                sx={{ '& .MuiBadge-badge': { bgcolor: '#fff', color: '#14B8A6', fontSize: '0.6rem', minWidth: 16, height: 16 } }}>
                Imagens
              </Badge>
            } icon={<ImageIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Procedimentos" icon={<MedicalIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
          </Tabs>
        </Box>
      </DialogTitle>

      {/* ── Conteúdo ── */}
      <DialogContent sx={{ p: 0, bgcolor: '#ffffff', overflowY: 'auto', flex: 1, minHeight: 0, WebkitOverflowScrolling: 'touch' }}>
        {summaryLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#14b8a6' }} />
          </Box>
        ) : (
          <>
            {/* ── TAB 0: PERFIL ── */}
            {tabValue === 0 && (
              <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, bgcolor: '#fafbfc' }}>
                {/* Alertas de Saúde */}
                {(patient.allergies?.length || patient.chronic_conditions?.length || patient.medications?.length ||
                  healthcareSummary?.patient?.allergies?.length || healthcareSummary?.patient?.chronic_conditions?.length || healthcareSummary?.patient?.medications?.length) ? (
                  <Card sx={{ mb: 2.5, border: '1px solid #fbbf24', borderRadius: 2, bgcolor: '#fffbeb' }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <WarningIcon sx={{ color: '#d97706', fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#92400e' }}>Alertas de Saúde</Typography>
                      </Box>
                      {(healthcareSummary?.patient?.allergies || patient.allergies || []).length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#dc2626' }}>Alergias:</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                            {(healthcareSummary?.patient?.allergies || patient.allergies || []).map((a: string, i: number) => (
                              <Chip key={i} label={a} size="small" sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontSize: '0.7rem', height: 22, fontWeight: 600 }} />
                            ))}
                          </Box>
                        </Box>
                      )}
                      {(healthcareSummary?.patient?.chronic_conditions || []).length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#d97706' }}>Condições Crônicas:</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                            {(healthcareSummary?.patient?.chronic_conditions || []).map((c: string, i: number) => (
                              <Chip key={i} label={c} size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontSize: '0.7rem', height: 22, fontWeight: 600 }} />
                            ))}
                          </Box>
                        </Box>
                      )}
                      {(healthcareSummary?.patient?.medications || []).length > 0 && (
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#2563eb' }}>Medicamentos em Uso:</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                            {(healthcareSummary?.patient?.medications || []).map((m: string, i: number) => (
                              <Chip key={i} label={m} size="small" sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontSize: '0.7rem', height: 22, fontWeight: 600 }} />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ) : null}

                {/* KPIs */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {[
                    { label: 'Consultas', value: profileConsultCount, color: '#0f766e' },
                    { label: 'Orçamentos', value: healthcareSummary?.stats?.total_budgets || 0, color: '#2563eb' },
                    { label: 'Receituários', value: healthcareSummary?.stats?.total_prescriptions || 0, color: '#7c3aed' },
                    { label: 'Total Investido', value: formatCurrency(profileTotalInvested), color: '#059669', isText: true },
                  ].map((kpi, i) => (
                    <Grid item xs={6} sm={3} key={i}>
                      <Card
                        sx={{
                          borderRadius: 3,
                          border: 'none',
                          textAlign: 'center',
                          bgcolor: '#fff',
                          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(15, 23, 42, 0.04)',
                        }}
                      >
                        <CardContent sx={{ py: 2, px: 1.25, '&:last-child': { pb: 2 } }}>
                          <Typography variant={kpi.isText ? 'body1' : 'h5'} sx={{ fontWeight: 800, color: kpi.color, fontSize: kpi.isText ? '0.85rem' : undefined }}>
                            {kpi.value}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{kpi.label}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* Dados Pessoais + Saúde + Endereço */}
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        border: 'none',
                        bgcolor: '#fff',
                        mb: 2.5,
                        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05), 0 1px 3px rgba(15, 23, 42, 0.04)',
                      }}
                    >
                      <CardContent sx={{ py: 2.5, px: 2.75, '&:last-child': { pb: 2.5 } }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#115e59', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon sx={{ fontSize: 18, color: '#0f766e' }} /> Dados Pessoais
                        </Typography>
                        <Grid container spacing={2}>
                          {[
                            { label: 'CPF', value: patient.cpf },
                            { label: 'RG', value: patient.rg },
                            { label: 'Data de Nascimento', value: patient.birth_date ? `${formatDate(patient.birth_date)} (${calculateAge(patient.birth_date)})` : null },
                            { label: 'Gênero', value: getGenderLabel(patient.gender || '') },
                            { label: 'Estado Civil', value: patient.marital_status },
                            { label: 'Profissão', value: patient.profession },
                            { label: 'Origem', value: patient.patient_source_ref?.name || patient.patient_source },
                          ].map((field, i) => field.value && field.value !== '-' ? (
                            <Grid item xs={6} key={i}>
                              <Typography variant="caption" color="text.secondary">{field.label}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{field.value}</Typography>
                            </Grid>
                          ) : null)}
                        </Grid>
                      </CardContent>
                    </Card>

                    <Card
                      sx={{
                        borderRadius: 3,
                        border: 'none',
                        bgcolor: '#fff',
                        mb: 2.5,
                        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05), 0 1px 3px rgba(15, 23, 42, 0.04)',
                      }}
                    >
                      <CardContent sx={{ py: 2.5, px: 2.75, '&:last-child': { pb: 2.5 } }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#115e59', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon sx={{ fontSize: 18, color: '#0f766e' }} /> Contatos
                        </Typography>
                        <Grid container spacing={2}>
                          {[
                            { label: 'Telefone', value: patient.phone },
                            { label: 'WhatsApp', value: patient.whatsapp },
                            { label: 'Tel. Secundário', value: patient.phone_secondary },
                            { label: 'Email', value: patient.email },
                          ].map((field, i) => field.value ? (
                            <Grid item xs={6} key={i}>
                              <Typography variant="caption" color="text.secondary">{field.label}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{field.value}</Typography>
                            </Grid>
                          ) : null)}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        border: 'none',
                        bgcolor: '#fff',
                        mb: 2.5,
                        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05), 0 1px 3px rgba(15, 23, 42, 0.04)',
                      }}
                    >
                      <CardContent sx={{ py: 2.5, px: 2.75, '&:last-child': { pb: 2.5 } }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#115e59', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <HeartIcon sx={{ fontSize: 18, color: '#0f766e' }} /> Saúde
                        </Typography>
                        <Grid container spacing={2}>
                          {patient.blood_type && (
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Tipo Sanguíneo</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                                <BloodIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#ef4444' }}>{patient.blood_type}</Typography>
                              </Box>
                            </Grid>
                          )}
                          {patient.last_visit_date && (
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Última Consulta</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{formatDate(patient.last_visit_date)}</Typography>
                            </Grid>
                          )}
                          {patient.first_visit_date && (
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Primeira Consulta</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{formatDate(patient.first_visit_date)}</Typography>
                            </Grid>
                          )}
                        </Grid>
                        {(healthcareSummary?.patient?.medical_notes || patient.medical_notes) && (
                          <Box sx={{ mt: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">Observações Médicas</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.82rem', bgcolor: '#f1f5f9', p: 1.5, borderRadius: 1.5, mt: 0.5 }}>
                              {healthcareSummary?.patient?.medical_notes || patient.medical_notes}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>

                    {(patient.insurance_plan || patient.clinic_insurances?.name) && (
                      <Card
                        sx={{
                          borderRadius: 3,
                          border: 'none',
                          bgcolor: '#fff',
                          mb: 2.5,
                          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05), 0 1px 3px rgba(15, 23, 42, 0.04)',
                        }}
                      >
                        <CardContent sx={{ py: 2.5, px: 2.75, '&:last-child': { pb: 2.5 } }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#115e59', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <HospitalIcon sx={{ fontSize: 18, color: '#0f766e' }} /> Convênio
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Plano</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                                {patient.clinic_insurances?.name || patient.insurance_plan}
                              </Typography>
                            </Grid>
                            {patient.insurance_number && (
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Nº Cartão</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem', fontFamily: 'monospace' }}>{patient.insurance_number}</Typography>
                              </Grid>
                            )}
                            {patient.insurance_validity && (
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Validade</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{formatDate(patient.insurance_validity)}</Typography>
                              </Grid>
                            )}
                          </Grid>
                        </CardContent>
                      </Card>
                    )}

                    {(patient.address_street || patient.address_city) && (
                      <Card
                        sx={{
                          borderRadius: 3,
                          border: 'none',
                          bgcolor: '#fff',
                          mb: 2.5,
                          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05), 0 1px 3px rgba(15, 23, 42, 0.04)',
                        }}
                      >
                        <CardContent sx={{ py: 2.5, px: 2.75, '&:last-child': { pb: 2.5 } }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#115e59', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FolderIcon sx={{ fontSize: 18, color: '#0f766e' }} /> Endereço
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.82rem', lineHeight: 1.6 }}>
                            {[patient.address_street, patient.address_number, patient.address_complement].filter(Boolean).join(', ')}<br />
                            {[patient.address_neighborhood, patient.address_city, patient.address_state].filter(Boolean).join(' - ')}
                            {patient.address_zip && ` • CEP: ${patient.address_zip}`}
                          </Typography>
                        </CardContent>
                      </Card>
                    )}
                  </Grid>
                </Grid>

                {patient.emergency_contact_name && (
                  <Card sx={{ borderRadius: 2, border: '1px solid #fca5a5', bgcolor: '#fff7f7', mt: 0 }}>
                    <CardContent sx={{ py: 1.5, px: 2.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <WarningIcon sx={{ fontSize: 18, color: '#dc2626' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#991b1b' }}>Contato de Emergência</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {patient.emergency_contact_name}
                        {patient.emergency_contact_relation && ` (${patient.emergency_contact_relation})`}
                        {patient.emergency_contact_phone && ` — ${patient.emergency_contact_phone}`}
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {patient.tags && patient.tags.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Tags</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {patient.tags.map((tag: string, i: number) => (
                        <Chip key={i} label={tag} size="small" sx={{ bgcolor: 'rgba(20,184,166,0.1)', color: '#0d9488', fontWeight: 600 }} />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* ── TAB 1: AGENDA ── */}
            {tabValue === 1 && (
              <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EventIcon sx={{ color: '#14B8A6', fontSize: 20 }} /> Próximas Consultas
                </Typography>
                {healthcareSummary?.upcoming_appointments?.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                    {healthcareSummary.upcoming_appointments.map((apt: any) => {
                      const st = getStatusConfig(apt.status);
                      return (
                        <Card key={apt.id} sx={{ borderRadius: 2, border: '1px solid rgba(20,184,166,0.15)', bgcolor: '#fff' }}>
                          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarIcon sx={{ fontSize: 16, color: '#14B8A6' }} />
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatDate(apt.appointment_date)}</Typography>
                                {apt.start_time && <Typography variant="body2" sx={{ color: '#14B8A6', fontWeight: 600 }}>{apt.start_time.substring(0, 5)}</Typography>}
                              </Box>
                              <Chip label={st.label} size="small" sx={{ bgcolor: st.bg, color: st.color, fontSize: '0.7rem', fontWeight: 700 }} />
                            </Box>
                            <Typography variant="body2" sx={{ color: '#475569' }}>{apt.title || apt.appointment_type}</Typography>
                            {apt.clinic_professionals && (
                              <Typography variant="caption" color="text.secondary">
                                Dr(a). {apt.clinic_professionals.full_name}{apt.clinic_professionals.specialty ? ` — ${apt.clinic_professionals.specialty}` : ''}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>Nenhuma consulta agendada.</Alert>
                )}

                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TimeIcon sx={{ color: '#64748b', fontSize: 20 }} /> Consultas Recentes
                </Typography>
                {healthcareSummary?.recent_appointments?.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {healthcareSummary.recent_appointments.map((apt: any) => {
                      const st = getStatusConfig(apt.status);
                      return (
                        <Box key={apt.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, px: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #f1f5f9' }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: st.color, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 90, fontSize: '0.8rem' }}>{formatDate(apt.appointment_date)}</Typography>
                          {apt.start_time && <Typography variant="body2" sx={{ color: '#14B8A6', fontWeight: 600, minWidth: 45, fontSize: '0.8rem' }}>{apt.start_time.substring(0, 5)}</Typography>}
                          <Typography variant="body2" sx={{ flex: 1, color: '#475569', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {apt.title || apt.appointment_type}
                          </Typography>
                          {apt.clinic_professionals && (
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100, textAlign: 'right', fontSize: '0.75rem' }}>
                              Dr(a). {apt.clinic_professionals.full_name}
                            </Typography>
                          )}
                          <Chip label={st.label} size="small" sx={{ bgcolor: st.bg, color: st.color, fontSize: '0.65rem', height: 22, fontWeight: 600 }} />
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>Nenhuma consulta registrada</Typography>
                )}
              </Box>
            )}

            {/* ── TAB 2: HISTÓRICO ── */}
            {tabValue === 2 && (
              <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                {/* Orçamentos */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DocIcon sx={{ color: '#2563eb', fontSize: 20 }} /> Orçamentos
                    </Typography>
                    <Chip label={healthcareSummary?.recent_budgets?.length || 0} size="small" sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 700 }} />
                  </Box>
                  {healthcareSummary?.recent_budgets?.length > 0 ? healthcareSummary.recent_budgets.map((b: any) => {
                    const st = getStatusConfig(b.status);
                    const rowKey = `bud:${b.id}`;
                    const isOpen = expandedHistoryRow === rowKey;
                    const bi = (b.items || []).slice().sort((a: any, x: any) => (a.sort_order ?? 0) - (x.sort_order ?? 0));
                    return (
                      <Box key={b.id} sx={{ py: 1, px: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #f1f5f9', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#2563eb', minWidth: 90, fontSize: '0.8rem' }}>{b.budget_number}</Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', minWidth: 80 }}>{formatDate(b.issue_date)}</Typography>
                          <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>{formatCurrency(b.total)}</Typography>
                          <Chip label={st.label} size="small" sx={{ bgcolor: st.bg, color: st.color, fontSize: '0.65rem', height: 22, fontWeight: 600 }} />
                          <Tooltip title={isOpen ? 'Recolher' : 'Ver itens'}>
                            <IconButton size="small" onClick={() => setExpandedHistoryRow(isOpen ? null : rowKey)} sx={{ color: '#64748b' }}>
                              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Collapse in={isOpen}>
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', mx: -2, px: 2, pb: 1.5, borderRadius: '0 0 8px 8px' }}>
                            {bi.length === 0 ? <Typography variant="caption" color="text.secondary">Nenhum item neste resumo.</Typography>
                              : bi.map((it: any, idx: number) => (
                                <Box key={it.id || idx} sx={{ py: 0.5, borderBottom: idx < bi.length - 1 ? '1px dashed #cbd5e1' : 'none' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1d4ed8', fontSize: '0.8rem' }}>{it.procedure_name || 'Procedimento'}</Typography>
                                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    Qtd {it.quantity ?? 1} × {formatCurrency(it.unit_price ?? 0)} → <strong>{formatCurrency(it.total ?? 0)}</strong>
                                  </Typography>
                                </Box>
                              ))}
                            <Button size="small" endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />} onClick={() => window.open('/clinic/budgets', '_blank')}
                              sx={{ mt: 1, textTransform: 'none', fontSize: '0.75rem' }}>
                              Abrir módulo de orçamentos
                            </Button>
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  }) : <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>Nenhum orçamento</Typography>}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Receituários */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MedicationIcon sx={{ color: '#7c3aed', fontSize: 20 }} /> Receituários
                    </Typography>
                    <Chip label={healthcareSummary?.recent_prescriptions?.length || 0} size="small" sx={{ bgcolor: '#ede9fe', color: '#6d28d9', fontWeight: 700 }} />
                  </Box>
                  {healthcareSummary?.recent_prescriptions?.length > 0 ? healthcareSummary.recent_prescriptions.map((p: any) => {
                    const rowKey = `rx:${p.id}`;
                    const isOpen = expandedHistoryRow === rowKey;
                    const meds = (p.items || []).slice().sort((a: any, x: any) => (a.sort_order ?? 0) - (x.sort_order ?? 0));
                    return (
                      <Box key={p.id} sx={{ py: 1, px: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #f1f5f9', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#7c3aed', minWidth: 90, fontSize: '0.8rem' }}>{p.prescription_number}</Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', minWidth: 80 }}>{formatDate(p.issue_date ?? p.prescription_date)}</Typography>
                          <Typography variant="body2" sx={{ flex: 1, color: '#475569', fontSize: '0.8rem' }}>{meds.length || 0} medicamento(s){p.professional_name && ` • Dr(a). ${p.professional_name}`}</Typography>
                          <Tooltip title={isOpen ? 'Recolher' : 'Ver medicamentos'}>
                            <IconButton size="small" onClick={() => setExpandedHistoryRow(isOpen ? null : rowKey)} sx={{ color: '#64748b' }}>
                              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Collapse in={isOpen}>
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e9d5ff', bgcolor: '#faf5ff', mx: -2, px: 2, pb: 1.5, borderRadius: '0 0 8px 8px' }}>
                            {meds.length === 0 ? <Typography variant="caption" color="text.secondary">Nenhum medicamento.</Typography>
                              : meds.map((m: any, idx: number) => (
                                <Box key={m.id || idx} sx={{ py: 0.5, borderBottom: idx < meds.length - 1 ? '1px dashed #ddd6fe' : 'none' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#5b21b6', fontSize: '0.8rem' }}>{m.medication_name || 'Medicamento'}</Typography>
                                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                    {[m.dosage, m.frequency, m.duration].filter(Boolean).join(' · ')}
                                  </Typography>
                                </Box>
                              ))}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  }) : <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>Nenhum receituário</Typography>}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Pedidos de Exames */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScienceIcon sx={{ color: '#059669', fontSize: 20 }} /> Pedidos de Exames
                    </Typography>
                    <Chip label={healthcareSummary?.recent_exam_orders?.length || 0} size="small" sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 700 }} />
                  </Box>
                  {healthcareSummary?.recent_exam_orders?.length > 0 ? healthcareSummary.recent_exam_orders.map((e: any) => {
                    const rowKey = `ex:${e.id}`;
                    const isOpen = expandedHistoryRow === rowKey;
                    const exItems = (e.items || []).slice().sort((a: any, x: any) => (a.sort_order ?? 0) - (x.sort_order ?? 0));
                    return (
                      <Box key={e.id} sx={{ py: 1, px: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #f1f5f9', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669', minWidth: 90, fontSize: '0.8rem' }}>{e.order_number}</Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', minWidth: 80 }}>{formatDate(e.issue_date ?? e.order_date)}</Typography>
                          <Typography variant="body2" sx={{ flex: 1, color: '#475569', fontSize: '0.8rem' }}>{exItems.length || 0} exame(s){e.professional_name && ` • Dr(a). ${e.professional_name}`}</Typography>
                          <Tooltip title={isOpen ? 'Recolher' : 'Ver exames'}>
                            <IconButton size="small" onClick={() => setExpandedHistoryRow(isOpen ? null : rowKey)} sx={{ color: '#64748b' }}>
                              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Collapse in={isOpen}>
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #a7f3d0', bgcolor: '#ecfdf5', mx: -2, px: 2, pb: 1.5, borderRadius: '0 0 8px 8px' }}>
                            {exItems.length === 0 ? <Typography variant="caption" color="text.secondary">Nenhum exame.</Typography>
                              : exItems.map((it: any, idx: number) => (
                                <Box key={it.id || idx} sx={{ py: 0.5, borderBottom: idx < exItems.length - 1 ? '1px dashed #6ee7b7' : 'none' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#047857', fontSize: '0.8rem' }}>{it.exam_name || 'Exame'}</Typography>
                                </Box>
                              ))}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  }) : <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>Nenhum pedido de exame</Typography>}
                </Box>

                {healthcareSummary?.active_treatment_plans?.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingIcon sx={{ color: '#14B8A6', fontSize: 20 }} /> Planos de Tratamento Ativos
                      </Typography>
                      {healthcareSummary.active_treatment_plans.map((tp: any) => {
                        const st = getStatusConfig(tp.status);
                        return (
                          <Box key={tp.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, px: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid rgba(20,184,166,0.15)', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#14B8A6', minWidth: 90, fontSize: '0.8rem' }}>{tp.plan_number || tp.title}</Typography>
                            <Typography variant="body2" sx={{ flex: 1, fontSize: '0.8rem' }}>{formatCurrency(tp.total_value)}</Typography>
                            <Chip label={st.label} size="small" sx={{ bgcolor: st.bg, color: st.color, fontSize: '0.65rem', height: 22, fontWeight: 600 }} />
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                )}

                {healthcareSummary?.recent_certificates?.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EventNoteIcon sx={{ color: '#f59e0b', fontSize: 20 }} /> Atestados Recentes
                      </Typography>
                      {healthcareSummary.recent_certificates.map((c: any) => (
                        <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, px: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #fef3c7', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#d97706', minWidth: 90, fontSize: '0.8rem' }}>{formatDate(c.issue_date)}</Typography>
                          <Typography variant="body2" sx={{ flex: 1, color: '#475569', fontSize: '0.8rem' }}>{c.purpose || c.certificate_type}</Typography>
                          {c.days_off && <Chip label={`${c.days_off} dia(s)`} size="small" sx={{ bgcolor: '#fef3c7', color: '#d97706', fontSize: '0.65rem', height: 22 }} />}
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </Box>
            )}

            {/* ── TAB 3: EVOLUÇÕES ── */}
            {tabValue === 3 && (
              <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimelineIcon sx={{ color: '#14B8A6', fontSize: 20 }} /> Timeline de Evoluções
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" startIcon={<AddIcon />}
                      onClick={() => {
                        setEvolutionExistingId(null);
                        setEvolutionDialogOpen(true);
                      }}
                      sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontSize: '0.75rem' }}>
                      Nova evolução
                    </Button>
                    <Button size="small" variant="outlined" href="/clinic/evolutions" target="_self"
                      sx={{ borderColor: '#14B8A6', color: '#14B8A6', textTransform: 'none', fontSize: '0.75rem' }}>
                      Ver Todas
                    </Button>
                  </Box>
                </Box>
                {!healthcareSummary?.recent_evolutions?.length ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>Nenhuma evolução registrada para este paciente.</Alert>
                ) : (
                  <Box sx={{ position: 'relative', pl: 3 }}>
                    <Box sx={{ position: 'absolute', left: 10, top: 0, bottom: 0, width: 2, bgcolor: '#e2e8f0' }} />
                    {healthcareSummary.recent_evolutions.map((evo: any) => (
                      <Box key={evo.id} sx={{ position: 'relative', mb: 2 }}>
                        <Box sx={{ position: 'absolute', left: -25, top: 4, width: 12, height: 12, borderRadius: '50%',
                          bgcolor: evo.is_signed ? '#22c55e' : '#f59e0b', border: '2px solid #fff', zIndex: 1 }} />
                        <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0', '&:hover': { borderColor: '#14B8A6' }, transition: 'all 0.2s' }}>
                          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f766e' }}>
                                  {evo.evolution_date ? new Date(evo.evolution_date + 'T12:00:00').toLocaleDateString('pt-BR') : '-'}
                                </Typography>
                                {evo.evolution_time && <Typography variant="caption" sx={{ color: '#94a3b8' }}>{evo.evolution_time.slice(0, 5)}</Typography>}
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip label={evo.is_signed ? 'Assinada' : 'Rascunho'} size="small"
                                  sx={{ fontSize: '0.65rem', height: 20, bgcolor: evo.is_signed ? '#dcfce7' : '#fef3c7', color: evo.is_signed ? '#16a34a' : '#d97706', fontWeight: 600 }} />
                                <Tooltip title="Imprimir ou editar nesta janela">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEvolutionExistingId(String(evo.id));
                                      setEvolutionDialogOpen(true);
                                    }}
                                    sx={{ color: '#14B8A6' }}
                                    aria-label="Imprimir ou editar esta evolução"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <IconButton size="small" onClick={() => setExpandedEvolutionId(prev => prev === evo.id ? null : evo.id)} sx={{ color: '#64748b' }}>
                                  {expandedEvolutionId === evo.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                              </Box>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
                              {evo.professional?.full_name || '-'}
                            </Typography>
                            {evo.diagnosis && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                {evo.diagnosis_code && <Chip label={evo.diagnosis_code} size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#ede9fe', color: '#7c3aed', fontWeight: 700 }} />}
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>{evo.diagnosis}</Typography>
                              </Box>
                            )}
                            {(() => {
                              const nProc = normalizeEvolutionProcedureLabels(evo).length;
                              const nMat = getEvolutionWorkflowPrintables(evo).length;
                              const hasRet = evolutionViewerHasFollowUpSection(evo);
                              if (!nProc && !nMat && !hasRet) return null;
                              const bits: string[] = [];
                              if (nProc) bits.push(`${nProc} indicação(ões)`);
                              if (nMat) bits.push(`${nMat} impresso(s)`);
                              if (hasRet) bits.push('retorno');
                              return (
                                <Typography variant="caption" sx={{ color: '#6366f1', display: 'block', mb: 0.35, fontWeight: 600 }}>
                                  {bits.join(' · ')}
                                </Typography>
                              );
                            })()}
                            {resolveEvolutionEntryFormat(evo) === 'free_text' ? (
                              evo.evolution_free_text && (
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 400 }}>
                                  <strong>Texto livre:</strong>{' '}
                                  {clinicalHtmlToPlainPreview(evo.evolution_free_text, 300)}
                                </Typography>
                              )
                            ) : evo.subjective ? (
                              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 400 }}>
                                <strong>S:</strong> {evo.subjective}
                              </Typography>
                            ) : null}
                            <Collapse in={expandedEvolutionId === evo.id} timeout="auto" unmountOnExit>
                              <Divider sx={{ my: 1.25 }} />
                              {resolveEvolutionEntryFormat(evo) === 'free_text' ? (
                                evo.evolution_free_text && (
                                  <Box sx={{ mb: 0.5 }}>
                                    <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'block', mb: 0.35 }}>
                                      Texto livre:
                                    </Typography>
                                    <ClinicalHtmlField
                                      html={evo.evolution_free_text}
                                      sx={{ fontSize: '0.75rem', color: '#475569', display: 'block' }}
                                    />
                                  </Box>
                                )
                              ) : (
                                <>
                                  {evo.objective && <Typography variant="caption" sx={{ color: '#475569', display: 'block', mb: 0.5, whiteSpace: 'pre-wrap' }}><strong>O:</strong> {evo.objective}</Typography>}
                                  {evo.assessment && <Typography variant="caption" sx={{ color: '#475569', display: 'block', mb: 0.5, whiteSpace: 'pre-wrap' }}><strong>A:</strong> {evo.assessment}</Typography>}
                                  {evo.plan && <Typography variant="caption" sx={{ color: '#475569', display: 'block', mb: 0.5, whiteSpace: 'pre-wrap' }}><strong>P:</strong> {evo.plan}</Typography>}
                                </>
                              )}
                              {normalizeEvolutionProcedureLabels(evo).length > 0 && (
                                <Box sx={{ mb: 0.85 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#3730a3', display: 'block', mb: 0.25 }}>
                                    Indicações:
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#475569', display: 'block', whiteSpace: 'pre-wrap' }}>
                                    {normalizeEvolutionProcedureLabels(evo).join(' · ')}
                                  </Typography>
                                </Box>
                              )}
                              {getEvolutionWorkflowPrintables(evo).length > 0 && (
                                <Box sx={{ mb: 0.85 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a', display: 'block', mb: 0.25 }}>
                                    Impressos / solicitações:
                                  </Typography>
                                  <Stack spacing={0.35}>
                                    {getEvolutionWorkflowPrintables(evo).map((p) => (
                                      <Typography key={p.clientKey} variant="caption" sx={{ color: '#475569' }}>
                                        • {evolutionPrintableListLabel(p)}
                                      </Typography>
                                    ))}
                                  </Stack>
                                </Box>
                              )}
                              {evolutionViewerHasFollowUpSection(evo) && (
                                <Box sx={{ mb: 0.5 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#b45309', display: 'block', mb: 0.25 }}>
                                    Retorno:
                                  </Typography>
                                  {formatEvolutionReturnWorkflowLines(getEvolutionReturnWorkflow(evo)).map((line) => (
                                    <Typography key={line} variant="caption" sx={{ color: '#475569', display: 'block' }}>
                                      • {line}
                                    </Typography>
                                  ))}
                                  {evo.follow_up_notes && (
                                    <Typography variant="caption" sx={{ color: '#475569', display: 'block', whiteSpace: 'pre-wrap', mt: 0.35 }}>
                                      {evo.follow_up_notes}
                                    </Typography>
                                  )}
                                  {evo.follow_up_date && (
                                    <Typography variant="caption" sx={{ color: '#475569', display: 'block', mt: 0.25 }}>
                                      Data:{' '}
                                      {new Date(evo.follow_up_date + 'T12:00:00').toLocaleDateString('pt-BR')}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Collapse>
                          </CardContent>
                        </Card>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* ── TAB 4: FINANCEIRO ── */}
            {tabValue === 4 && (
              <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={4}>
                    <Card sx={{ borderRadius: 2, border: '1px solid #d1fae5', bgcolor: '#f0fdf4', textAlign: 'center' }}>
                      <CardContent sx={{ py: 2, px: 1, '&:last-child': { pb: 2 } }}>
                        <Typography variant="caption" sx={{ color: '#059669', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Total Pago</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#059669' }}>{formatCurrency(healthcareSummary?.financial?.total_paid || 0)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card sx={{ borderRadius: 2, border: '1px solid #fef3c7', bgcolor: '#fffbeb', textAlign: 'center' }}>
                      <CardContent sx={{ py: 2, px: 1, '&:last-child': { pb: 2 } }}>
                        <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Pendente</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#d97706' }}>{formatCurrency(healthcareSummary?.financial?.total_pending || 0)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card sx={{ borderRadius: 2, border: '1px solid #fee2e2', bgcolor: '#fef2f2', textAlign: 'center' }}>
                      <CardContent sx={{ py: 2, px: 1, '&:last-child': { pb: 2 } }}>
                        <Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Em Atraso</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#dc2626' }}>{formatCurrency(healthcareSummary?.financial?.total_overdue || 0)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Card sx={{ borderRadius: 2, border: '1px solid rgba(20,184,166,0.2)', mb: 2, bgcolor: 'rgba(20,184,166,0.04)' }}>
                  <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 }, textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#0d9488', fontWeight: 700, textTransform: 'uppercase' }}>Total Investido pelo Paciente</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#14B8A6', mt: 0.5 }}>{formatCurrency(profileTotalInvested)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      em {profileConsultCount} consulta(s){patient.first_visit_date && ` • desde ${formatDate(patient.first_visit_date)}`}
                    </Typography>
                  </CardContent>
                </Card>

                {/* Contas a receber do paciente */}
                <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                  <CardContent sx={{ p: { xs: 1.25, sm: 2 }, '&:last-child': { pb: { xs: 1.25, sm: 2 } } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.25 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <MoneyIcon sx={{ fontSize: 18, color: '#10b981' }} />
                        Contas a Receber do Paciente
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.75 }}>
                        <Tooltip title="Atualizar">
                          <IconButton size="small" onClick={loadReceivables} disabled={receivablesLoading} sx={{ color: '#10b981' }}>
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openCreateReceivable}
                          sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, textTransform: 'none', fontWeight: 600 }}>
                          Nova Conta
                        </Button>
                      </Box>
                    </Box>

                    {receivablesLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={22} sx={{ color: '#10b981' }} />
                      </Box>
                    ) : receivables.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#f8fafc', borderRadius: 2, border: '1px dashed #e2e8f0' }}>
                        <Typography variant="body2" color="text.secondary">Nenhuma conta a receber para este paciente.</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Use “Nova Conta” para registrar um valor a receber e, se quiser, marcar como recebido na hora — cai automaticamente no caixa do dia.
                        </Typography>
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                              <TableCell sx={{ fontWeight: 700 }} align="right">Valor</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Vencimento</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Pagto</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                              <TableCell sx={{ fontWeight: 700 }} align="right">Ações</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {receivables.map((rec: any) => {
                              const st = getReceivableStatusInfo(rec.status);
                              const isPaid = rec.status === 'paid';
                              const remaining = Math.max(0, (Number(rec.amount) || 0) - (Number(rec.amount_paid || rec.paid_amount) || 0));
                              return (
                                <TableRow key={rec.id} hover>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{rec.description}</Typography>
                                    {rec.category && (
                                      <Typography variant="caption" color="text.secondary">
                                        {RECEIVABLE_CATEGORIES.find(c => c.value === rec.category)?.label || rec.category}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(rec.amount)}</Typography>
                                    {(rec.amount_paid || rec.paid_amount) > 0 && (
                                      <Typography variant="caption" sx={{ color: '#059669', display: 'block' }}>
                                        Pago: {formatCurrency(rec.amount_paid || rec.paid_amount || 0)}
                                      </Typography>
                                    )}
                                    {rec.status === 'partial' && remaining > 0 && (
                                      <Typography variant="caption" sx={{ color: '#d97706', display: 'block', fontWeight: 600 }}>
                                        Saldo: {formatCurrency(remaining)}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">{formatDate(rec.due_date)}</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption">{getPaymentMethodLabel(rec.payment_method)}</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip label={st.label} size="small" sx={{ bgcolor: st.bg, color: st.color, fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                                  </TableCell>
                                  <TableCell align="right">
                                    {!isPaid && (
                                      <Tooltip title={remaining > 0 ? `Receber (saldo: ${formatCurrency(remaining)})` : 'Receber agora'}>
                                        <span>
                                          <IconButton size="small" onClick={() => openReceiveDialog(rec)}
                                            sx={{ color: '#10b981' }} disabled={remaining <= 0}>
                                            <MoneyIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Excluir">
                                      <IconButton size="small" onClick={() => deleteReceivable(rec)} sx={{ color: '#dc2626' }}>
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* ── TAB 5: DOCUMENTOS ── */}
            {tabValue === 5 && (
              <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FolderDocIcon sx={{ color: '#2563eb' }} /> Documentos do Paciente
                  </Typography>
                  <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => setShowDocUpload(true)}
                    sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' }, textTransform: 'none', borderRadius: 2 }}>
                    Anexar Documento
                  </Button>
                </Box>

                {showDocUpload && (
                  <Card sx={{ mb: 3, borderRadius: 2, border: '1px solid rgba(37,99,235,0.2)', bgcolor: 'rgba(37,99,235,0.03)' }}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2563eb', mb: 2 }}>Novo Documento</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth size="small" label="Título" value={docUploadForm.title}
                            onChange={e => setDocUploadForm(p => ({ ...p, title: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Tipo de Documento</InputLabel>
                            <Select value={docUploadForm.document_type} label="Tipo de Documento"
                              onChange={e => setDocUploadForm(p => ({ ...p, document_type: e.target.value }))}>
                              <MenuItem value="contract">Contrato</MenuItem>
                              <MenuItem value="consent_form">Termo de Consentimento</MenuItem>
                              <MenuItem value="id_document">Documento de Identidade</MenuItem>
                              <MenuItem value="insurance_card">Carteirinha de Plano</MenuItem>
                              <MenuItem value="exam_result">Resultado de Exame</MenuItem>
                              <MenuItem value="xray">Raio-X / Imagem Clínica</MenuItem>
                              <MenuItem value="other">Outro</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField fullWidth size="small" label="Descrição (opcional)" value={docUploadForm.description}
                            onChange={e => setDocUploadForm(p => ({ ...p, description: e.target.value }))} />
                        </Grid>
                      </Grid>
                      <input type="file" ref={docFileInputRef} style={{ display: 'none' }}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.txt,.xls,.xlsx"
                        onChange={e => {
                          const f = e.target.files?.[0];
                          if (f) { setDocFile(f); if (!docUploadForm.title) setDocUploadForm(p => ({ ...p, title: f.name })); }
                        }}
                      />
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
                        <Button variant="outlined" startIcon={<AttachFileIcon />} onClick={() => docFileInputRef.current?.click()}
                          sx={{ borderColor: '#2563eb', color: '#2563eb', textTransform: 'none', borderRadius: 2 }}>
                          {docFile ? docFile.name : 'Selecionar arquivo'}
                        </Button>
                        {docFile && <Typography variant="caption" color="text.secondary">{(docFile.size / 1024).toFixed(0)} KB</Typography>}
                        <Box sx={{ flex: 1 }} />
                        <Button onClick={() => { setShowDocUpload(false); setDocFile(null); }} sx={{ textTransform: 'none', color: '#64748b' }}>Cancelar</Button>
                        <Button variant="contained" disabled={!docFile || docUploading} onClick={handleDocUpload}
                          sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' }, textTransform: 'none', borderRadius: 2 }}>
                          {docUploading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Salvar'}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {docsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#14B8A6' }} /></Box>
                ) : documents.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#fff', borderRadius: 3, border: '2px dashed #e2e8f0' }}>
                    <FolderDocIcon sx={{ fontSize: 56, color: '#e2e8f0', mb: 1.5 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>Nenhum documento anexado</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {documents.map((doc: any) => (
                      <Card key={doc.id} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ width: 44, height: 44, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${getDocTypeColor(doc.document_type)}15`, flexShrink: 0 }}>
                            <FileIcon sx={{ fontSize: 22, color: getDocTypeColor(doc.document_type) }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.title}</Typography>
                            <Chip label={getDocTypeLabel(doc.document_type)} size="small"
                              sx={{ bgcolor: `${getDocTypeColor(doc.document_type)}15`, color: getDocTypeColor(doc.document_type), fontSize: '0.65rem', height: 20, fontWeight: 600 }} />
                          </Box>
                          <Tooltip title="Baixar / Visualizar">
                            <IconButton onClick={() => window.open(doc.file_url, '_blank')} sx={{ color: '#2563eb' }}><DownloadIcon /></IconButton>
                          </Tooltip>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* ── TAB 6: IMAGENS ── */}
            {tabValue === 6 && (
              <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ImageIcon sx={{ color: '#14B8A6' }} /> Banco de Imagens Clínicas
                  </Typography>
                  <Chip label={`${patientImages.length} imagem(ns)`} size="small" sx={{ bgcolor: 'rgba(20,184,166,0.1)', color: '#14B8A6', fontWeight: 600 }} />
                </Box>
                {imagesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#14B8A6' }} /></Box>
                ) : patientImages.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#fff', borderRadius: 3, border: '2px dashed #e2e8f0' }}>
                    <ImageIcon sx={{ fontSize: 56, color: '#e2e8f0', mb: 1.5 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>Nenhuma imagem clínica</Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {patientImages.map((img: any) => (
                      <Grid item xs={6} sm={4} md={3} key={img.id}>
                        <Card sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0', cursor: 'pointer', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.12)', borderColor: '#14B8A6' } }}
                          onClick={() => setExpandedImage(img.file_url)}>
                          <Box sx={{ position: 'relative', paddingTop: '75%', bgcolor: '#f8fafc' }}>
                            <img src={img.file_url} alt={img.title} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.opacity = '0.3'; }} />
                            <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
                              {img.is_favorite && <StarIcon sx={{ fontSize: 16, color: '#f59e0b', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }} />}
                              <ZoomInIcon sx={{ fontSize: 16, color: '#fff', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.8))' }} />
                            </Box>
                          </Box>
                          <Box sx={{ p: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{img.title}</Typography>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}

            {/* ── TAB 7: PROCEDIMENTOS ── */}
            {tabValue === 7 && (
              <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MedicalIcon sx={{ color: '#14B8A6' }} /> Procedimentos do Paciente
                  </Typography>
                  <IconButton size="small" onClick={loadPatientProcedures}><RefreshIcon sx={{ fontSize: 18 }} /></IconButton>
                </Box>
                {proceduresLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#14B8A6' }} /></Box>
                ) : (
                  <>
                    {/* Aprovados */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#3b82f6' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e40af' }}>
                          Liberados — aprovados para executar ({patientProcedures.approved.length})
                        </Typography>
                      </Box>
                      {patientProcedures.approved.length === 0 ? (
                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, borderStyle: 'dashed' }}>
                          <Typography variant="body2" color="text.secondary">Nenhum liberado.</Typography>
                        </Paper>
                      ) : (
                        <Stack spacing={1}>
                          {patientProcedures.approved.map((item: any) => (
                            <Paper key={item.id} variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #3b82f6' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{item.procedure_name}</Typography>
                                  <Typography variant="caption" color="text.secondary">Plano: {item.plan_title || item.plan_number}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                                  <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <InputLabel>Status</InputLabel>
                                    <Select label="Status" value={item.status === 'cancelled' ? 'cancelled' : item.status || 'in_progress'}
                                      onChange={(e) => handleProcedureItemStatusChange(item.id, e.target.value as string)}>
                                      <MenuItem value="pending">Pendente</MenuItem>
                                      <MenuItem value="in_progress">Aprovado para executar</MenuItem>
                                      <MenuItem value="cancelled">Cancelar item</MenuItem>
                                    </Select>
                                  </FormControl>
                                  <Button size="small" variant="contained" startIcon={<PlayIcon />}
                                    disabled={item.status !== 'in_progress'} onClick={() => handleExecuteProcedure(item)}
                                    sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }}>
                                    Executar
                                  </Button>
                                </Box>
                              </Box>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </Box>

                    {/* Pendentes */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#92400e' }}>
                          Pendentes — aguardando liberação ({patientProcedures.pending.length})
                        </Typography>
                      </Box>
                      {patientProcedures.pending.length === 0 ? (
                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, borderStyle: 'dashed' }}>
                          <Typography variant="body2" color="text.secondary">Nenhum pendente</Typography>
                        </Paper>
                      ) : (
                        <Stack spacing={1}>
                          {patientProcedures.pending.map((item: any) => (
                            <Paper key={item.id} variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #f59e0b', opacity: 0.85 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{item.procedure_name}</Typography>
                                  <Typography variant="caption" color="text.secondary">Plano: {item.plan_title || item.plan_number}</Typography>
                                </Box>
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                  <InputLabel>Status</InputLabel>
                                  <Select label="Status" value={item.status === 'cancelled' ? 'cancelled' : item.status || 'pending'}
                                    onChange={(e) => handleProcedureItemStatusChange(item.id, e.target.value as string)}>
                                    <MenuItem value="pending">Pendente</MenuItem>
                                    <MenuItem value="in_progress">Aprovado para executar</MenuItem>
                                    <MenuItem value="cancelled">Cancelar item</MenuItem>
                                  </Select>
                                </FormControl>
                              </Box>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </Box>

                    {/* Executados */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#10b981' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#065f46' }}>
                          Executados ({patientProcedures.completed.length})
                        </Typography>
                      </Box>
                      {patientProcedures.completed.length === 0 ? (
                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, borderStyle: 'dashed' }}>
                          <Typography variant="body2" color="text.secondary">Nenhum executado ainda</Typography>
                        </Paper>
                      ) : (
                        <Stack spacing={1}>
                          {patientProcedures.completed.map((item: any) => {
                            const exec = item.execution;
                            const consumed: any[] = exec?.consumed_materials || [];
                            return (
                              <Paper key={item.id} variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #10b981' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <CheckCircleIcon sx={{ fontSize: 18, color: '#10b981' }} />
                                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{item.procedure_name}</Typography>
                                    </Box>
                                    {exec?.executed_at && (
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        Executado em: {new Date(exec.executed_at).toLocaleDateString('pt-BR')}
                                      </Typography>
                                    )}
                                    {consumed.length > 0 && (
                                      <Box sx={{ mt: 0.75 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280' }}>Materiais consumidos:</Typography>
                                        {consumed.map((c: any, idx: number) => (
                                          <Typography key={idx} variant="caption" display="block" sx={{ ml: 2, color: '#374151' }}>• {c.item_name}: {c.quantity} {c.unit}</Typography>
                                        ))}
                                      </Box>
                                    )}
                                  </Box>
                                  <Tooltip title="Desfazer execução">
                                    <IconButton size="small" onClick={() => handleUndoExecution(item.id)} sx={{ color: '#ef4444' }}>
                                      <UndoIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Paper>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>

                    {patientProcedures.pending.length === 0 && patientProcedures.approved.length === 0 && patientProcedures.completed.length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#fff', borderRadius: 3, border: '2px dashed #e2e8f0' }}>
                        <MedicalIcon sx={{ fontSize: 56, color: '#e2e8f0', mb: 1.5 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>Nenhum procedimento vinculado</Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* ── Sub-dialogs ── */}

      {/* Executar procedimento */}
      <Dialog open={executeDialogOpen} onClose={() => setExecuteDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Executar Procedimento
          <IconButton onClick={() => setExecuteDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {executingItem && (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{executingItem.procedure_name}</Typography>
              {executingItem.required_materials?.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <InventoryIcon sx={{ fontSize: 18, color: '#8b5cf6' }} /> Materiais que serão consumidos
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Consumo</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Estoque</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Após</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {executingItem.required_materials.map((m: any) => {
                          const inv = m.clinic_inventory_items;
                          const needed = (m.quantity_per_execution || 1) * (executingItem.quantity || 1);
                          const current = Number(inv?.current_stock ?? 0);
                          const after = current - needed;
                          const negative = after < 0;
                          return (
                            <TableRow key={m.id}>
                              <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{inv?.name || '?'}</Typography></TableCell>
                              <TableCell align="center">{needed} {inv?.unit || ''}</TableCell>
                              <TableCell align="center">{current}</TableCell>
                              <TableCell align="center" sx={{ color: negative ? '#dc2626' : '#059669', fontWeight: 700 }}>
                                {after.toFixed(1)} {negative && <WarningIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Profissional executor *</InputLabel>
                <Select value={executeForm.professional_id} label="Profissional executor *"
                  onChange={(e) => setExecuteForm({ ...executeForm, professional_id: e.target.value })}>
                  {professionals.map((p: any) => (
                    <MenuItem key={p.id} value={p.id}>{p.full_name}{p.specialty ? ` — ${p.specialty}` : ''}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField fullWidth size="small" label="Observações (opcional)" multiline rows={2}
                value={executeForm.notes} onChange={(e) => setExecuteForm({ ...executeForm, notes: e.target.value })} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setExecuteDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={confirmExecute} disabled={executeSaving || !executeForm.professional_id}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }}>
            {executeSaving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Confirmar Execução'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lightbox imagens */}
      <Dialog open={!!expandedImage} onClose={() => setExpandedImage(null)} maxWidth="lg" fullScreen={isMobile}
        PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.92)', boxShadow: 'none', m: isMobile ? 0 : 1 } }}>
        <Box sx={{ position: 'relative' }}>
          <IconButton onClick={() => setExpandedImage(null)}
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
            <CloseIcon />
          </IconButton>
          {expandedImage && <img src={expandedImage} alt="Ampliada" style={{ maxWidth: '90vw', maxHeight: '85vh', display: 'block', objectFit: 'contain' }} />}
        </Box>
      </Dialog>

      {/* HealthcarePanel */}
      <HealthcarePanel
        open={healthcarePanelOpen}
        onClose={() => setHealthcarePanelOpen(false)}
        contactId={patient?.contact_id || null}
        contactName={patient?.full_name}
        contactPhone={patient?.whatsapp || patient?.phone}
        contactEmail={patient?.email}
      />

      {/* HealthcareEvolutionDialog */}
      <HealthcareEvolutionDialog
        open={evolutionDialogOpen}
        onClose={() => {
          setEvolutionDialogOpen(false);
          setEvolutionExistingId(null);
        }}
        existingEvolutionId={evolutionExistingId}
        patient={patient ? { id: patient.id, full_name: patient.full_name } : null}
        onSaved={async () => {
          await fetchSummary();
          setSnackMsg(evolutionExistingId ? 'Evolução atualizada com sucesso!' : 'Evolução criada com sucesso!');
        }}
        onError={(msg) => setSnackMsg(msg)}
      />

      {/* ── Dialog: Nova Conta a Receber ── */}
      <Dialog open={createReceivableOpen} onClose={() => !creatingReceivable && setCreateReceivableOpen(false)}
        maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MoneyIcon sx={{ color: '#10b981' }} /> Nova Conta a Receber
          </Box>
          <IconButton onClick={() => !creatingReceivable && setCreateReceivableOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {patient && (
            <Box sx={{ mb: 1.5, p: 1, borderRadius: 1.5, bgcolor: '#f0fdfa', border: '1px solid #ccfbf1', display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon sx={{ fontSize: 18, color: '#0d9488' }} />
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0d9488' }}>{patient.full_name}</Typography>
            </Box>
          )}
          <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" required label="Descrição"
                value={receivableForm.description}
                onChange={(e) => setReceivableForm({ ...receivableForm, description: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" required label="Valor (R$)" type="number" inputProps={{ min: 0, step: '0.01' }}
                value={receivableForm.amount}
                onChange={(e) => setReceivableForm({ ...receivableForm, amount: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" required label="Vencimento" type="date" InputLabelProps={{ shrink: true }}
                value={receivableForm.due_date}
                onChange={(e) => setReceivableForm({ ...receivableForm, due_date: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Categoria</InputLabel>
                <Select label="Categoria" value={receivableForm.category}
                  onChange={(e) => setReceivableForm({ ...receivableForm, category: String(e.target.value) })}>
                  {RECEIVABLE_CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Forma de Recebimento</InputLabel>
                <Select label="Forma de Recebimento" value={receivableForm.payment_method}
                  onChange={(e) => setReceivableForm({ ...receivableForm, payment_method: String(e.target.value) })}>
                  {RECEIVABLE_PAYMENT_METHODS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Observações" multiline rows={2}
                value={receivableForm.notes}
                onChange={(e) => setReceivableForm({ ...receivableForm, notes: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1.5, bgcolor: receivableForm.receive_now ? '#ecfdf5' : '#f8fafc', border: '1px solid', borderColor: receivableForm.receive_now ? '#a7f3d0' : '#e2e8f0' }}>
                <input id="receive-now-chk" type="checkbox" checked={receivableForm.receive_now}
                  onChange={(e) => setReceivableForm({ ...receivableForm, receive_now: e.target.checked })}
                  style={{ width: 16, height: 16, accentColor: '#10b981' }} />
                <label htmlFor="receive-now-chk" style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a', cursor: 'pointer' }}>
                  Receber agora — lançar valor no caixa do dia
                </label>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setCreateReceivableOpen(false)} disabled={creatingReceivable} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={submitCreateReceivable} disabled={creatingReceivable}
            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, textTransform: 'none', fontWeight: 700 }}>
            {creatingReceivable ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : (receivableForm.receive_now ? 'Criar e Receber' : 'Criar Conta')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Receber Agora ── */}
      <Dialog open={receiveDialogOpen} onClose={() => !receivingReceivable && setReceiveDialogOpen(false)}
        maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MoneyIcon sx={{ color: '#10b981' }} /> Registrar Recebimento
          </Box>
          <IconButton onClick={() => !receivingReceivable && setReceiveDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {activeReceivable && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{activeReceivable.description}</Typography>
              <Typography variant="caption" color="text.secondary">
                Total: {formatCurrency(activeReceivable.amount)} · Pago: {formatCurrency(activeReceivable.amount_paid || 0)}
              </Typography>
            </Box>
          )}
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <TextField fullWidth size="small" required label="Valor recebido (R$)" type="number" inputProps={{ min: 0, step: '0.01' }}
              value={receiveForm.amount}
              onChange={(e) => setReceiveForm({ ...receiveForm, amount: e.target.value })} />
            <TextField fullWidth size="small" required label="Data" type="date" InputLabelProps={{ shrink: true }}
              value={receiveForm.payment_date}
              onChange={(e) => setReceiveForm({ ...receiveForm, payment_date: e.target.value })} />
            <FormControl fullWidth size="small">
              <InputLabel>Forma de Recebimento</InputLabel>
              <Select label="Forma de Recebimento" value={receiveForm.payment_method}
                onChange={(e) => setReceiveForm({ ...receiveForm, payment_method: String(e.target.value) })}>
                {RECEIVABLE_PAYMENT_METHODS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth size="small" label="Observações (opcional)" multiline rows={2}
              value={receiveForm.notes}
              onChange={(e) => setReceiveForm({ ...receiveForm, notes: e.target.value })} />
            <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: '0.78rem' } }}>
              Ao confirmar, o valor é lançado como entrada no <strong>caixa do dia</strong> (precisa estar aberto).
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setReceiveDialogOpen(false)} disabled={receivingReceivable} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={submitReceive} disabled={receivingReceivable}
            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, textTransform: 'none', fontWeight: 700 }}>
            {receivingReceivable ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Confirmar Recebimento'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback rápido em tela */}
      {snackMsg && (
        <Alert
          severity="info"
          onClose={() => setSnackMsg('')}
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, maxWidth: 400 }}
        >
          {snackMsg}
        </Alert>
      )}
    </Box>
  );
};

export default PatientDetailPanel;
